import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type {
  UseQueryOptions,
  UseQueryResult,
  QueryKey,
} from '@tanstack/react-query';
import service from '../service';

export type Activity = {
  identifier: string;
  createdAt: string;
  type: string;
  typeDescriptor?: string;
  status: string;
  price?: number;
  attachedOrder?: object;
  typeMeta?: Record<string, any>;
  future?: boolean;
  activityDate?: string;
};

/** Lifetime counts of completed activity, faceted by type. */
export type ActivityTotals = {
  hotelStays: number;
  bookedEvents: number;
  ticketedEvents: number;
  restaurantReservations: number;
};

export type ActivityMeta = {
  mtdTotal: number;
  ytdTotal: number;
  allTotal: number;
  activityTotals?: ActivityTotals;
};

export type ActivityResponse = {
  data: Activity[];
  meta: ActivityMeta;
};

type UseActivityParams = {
  fishId: string | undefined;
  enabled?: boolean;
};

type DefaultError = unknown;

/**
 * useActivity
 *
 * Fetches activity data for a fish/member with showCancelled toggle.
 *
 * @example
 * const {
 *   activity,
 *   upcomingCount,
 *   showCancelled,
 *   setShowCancelled,
 *   isLoading,
 * } = useActivity({ fishId: actor.id });
 */
export function useActivity<TData = ActivityResponse, TError = DefaultError>(
  { fishId, enabled = true }: UseActivityParams,
  options?: Omit<
    UseQueryOptions<ActivityResponse, TError, TData, QueryKey>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
): UseQueryResult<TData, TError> & {
  activity: ActivityResponse | undefined;
  upcomingCount: number;
  showCancelled: boolean;
  setShowCancelled: (show: boolean) => void;
} {
  const [showCancelled, setShowCancelled] = useState(false);

  const queryResult = useQuery<ActivityResponse, TError, TData>({
    queryKey: ['activity', fishId, showCancelled],
    enabled: enabled && !!fishId,
    retry: 1,
    queryFn: async () => {
      const res = await service.getActivity(fishId!, showCancelled);
      if (!res.ok) {
        throw new Error('Failed to fetch activity');
      }
      return res.json();
    },
    ...(options as any),
  });

  const activity = queryResult.data as ActivityResponse | undefined;

  const upcomingCount = useMemo(() => {
    if (!activity?.data) return 0;
    return activity.data.filter(
      (item) => item.future && item.status !== 'PROSPECT',
    ).length;
  }, [activity?.data]);

  return {
    ...queryResult,
    activity,
    upcomingCount,
    showCancelled,
    setShowCancelled,
  };
}
